/**
 * MarsadIcon (P283.1) -- renders one of the Marsad Marks (the Observatory
 * icon library, design-library/icons/). The 30 SVG inner bodies come from
 * `shared/icons/marks.generated.ts`, which is generated from the same
 * manifest that emits the standalone SVGs + the gallery -- one source of
 * truth, no drift.
 *
 * The language (held by the marks themselves): 24x24 grid, 1.5 stroke,
 * round caps, currentColor; at most one brass crown per mark marking "the
 * observed thing". This component only provides the frame + a11y -- it
 * never recolours a mark beyond `currentColor` (the One Rule: colour is
 * the caller's job via `text-*`, and brass=interaction / rag=status holds).
 *
 * Decorative by default (`aria-hidden`, paired with a text label). Pass
 * `title` for standalone use -> role="img" + aria-label. RTL: marks in
 * `MIRROR_MARKS` flip when `mirror` is set (wire from `dir="rtl"`).
 */
import { MARKS, MIRROR_MARKS, type MarkName } from "shared/icons/marks.generated";

interface Props {
  name: MarkName;
  /** Rendered px (width == height). Default 20. Floor 16 in practice. */
  size?: number;
  /** Passed through for colour + spacing, e.g. "text-brand-brass". */
  className?: string;
  /** When set, the mark is meaningful on its own: role=img + this label. */
  title?: string;
  /** Mirror horizontally (for direction-carrying marks under dir=rtl). */
  mirror?: boolean;
}

export function MarsadIcon({
  name,
  size = 20,
  className,
  title,
  mirror = false,
}: Props): JSX.Element {
  const flip = mirror && MIRROR_MARKS.has(name);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={flip ? { transform: "scaleX(-1)" } : undefined}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      // Static, first-party icon bodies from the generated manifest --
      // never user input. kebab-case attrs are correct in innerHTML.
      dangerouslySetInnerHTML={{ __html: MARKS[name] }}
    />
  );
}
